// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.MapFrontSide.js" company="Izenda">
// Copyright (c) 2015 Izenda, Inc.  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and  International
// Copyright Laws. Unauthorized reproduction,  reverse-engineering, and
// distribution of all or any portion of  the code contained in this file is
// strictly prohibited and may  result in severe civil and criminal penalties
// and will be  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES  ARE CONFIDENTIAL AND
// PROPRIETARY TRADE  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
// LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET  CONTROLS AS
// PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED  FILES OR ANY
// PORTION OF ITS CONTENTS SHALL AT NO TIME BE  COPIED, TRANSFERRED, SOLD,
// DISTRIBUTED, OR OTHERWISE MADE  AVAILABLE TO OTHER INDIVIDUALS WITHOUT
// EXPRESS WRITTEN CONSENT  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON  ADDITIONAL
// RESTRICTIONS.
//
// </copyright>
// ----------------------------------------------------------------------

/**
 * GaugeFrontSide component
 */
import connect from 'share/enhancers/Izenda.Connect';
import React, { Component } from 'react';
import { observer } from 'mobx-react';
import { action } from 'mobx';
import classnames from 'classnames';
import Chart from 'share/components/charting/Izenda.Chart';
import { parser as chartDataParser } from 'share/utils/Izenda.ChartDataParser';
import ReportPartTitle from '../FieldContainers/Izenda.ReportPartTitle';
import MapToolbar from './Izenda.MapToolbar';
import CONSTANT from 'logic/data/Izenda.ReportBodyConstants';
import ProvisionMapDataStore from 'logic/Izenda.ProvisionMapDataStore';
import uiState from 'logic/Izenda.UiState';
import PrintingUtils from 'share/utils/Izenda.PrintingUtils';
import ReportPartUtils from 'share/utils/Izenda.ReportPartUtils';
import VerticalLayout from 'share/components/controls/Izenda.VerticalLayout';
import { isEqual, getOr, invoke, flow, get, isNil, merge, find, isEmpty, forEach, omit } from 'lodash/fp';

const REPORT_PART_MODE = CONSTANT.REPORT_PART_MODE;

@observer
class MapChart extends Component {
  componentWillReceiveProps(nextProps) {
    nextProps.isReloadData && nextProps.reloadData();
  }

  render() {
    return <Chart {...this.props} />;
  }
}

@observer
class MapPreview extends Component {

  constructor(props) {
    super(props);

    this.saveCurrentReportState(getOr({}, 'reportPart.reportPartContent', props));
    this.state = {
      shownRecords: []
    };
  }

  componentDidMount() {
    this.updateDrillComponent();
  }

  shouldComponentUpdate(nextProps: Object, nextState) {
    return !isEqual(this.props, nextProps) || !isEqual(this.state, nextState);
  }

  componentDidUpdate() {
    this.updateDrillComponent();
  }

  getChartProperties(props) {
    const {
      printingMode,
      pointAction
    } = props;
    const reportPart = props.reportPart || {};
    const reportPartContent = reportPart.reportPartContent;
    let chartProperties = reportPartContent.getMapOptions() || {};
    chartProperties = merge(chartProperties, {
      colors: reportPart.getThemeColors(chartProperties),
      commonOptions: {},
      commonActions: {
        onDrilldown: this.onDrilldown,
        pointAction: pointAction
      },
      changeZoomLevel: zoom => reportPartContent.changeZoomLevel(zoom),
      changeCenterPoint: center => reportPartContent.changeCenterPoint(center)
    });

    // turn off animation when printing.
    if (printingMode) {
      chartProperties.commonOptions = merge(chartProperties.commonOptions, {
        izendaOptions: {
          printingMode: printingMode
        }
      });
    }
    return chartProperties;
  }

  saveCurrentReportState = reportPartContent => {
    this.activePointOptionByView = reportPartContent.activePointOption;
    this.isBeingBuildByView = reportPartContent.isBeingBuild;
    this.previousMapType = reportPartContent.chartType;
  };

  updateDrillComponent = () => {
    const reportPart = getOr({}, 'reportPart', this.props);
    reportPart.onDrilldown = this.onDrilldown;
  };

  reloadData = () => {
    const { printingMode, publishDrillInfo, store, needUpdatePreview } = this.props;
    const reportPart = this.props.reportPart || {};
    const reportPartContent = reportPart.reportPartContent;
    !!publishDrillInfo && publishDrillInfo(reportPart.id, reportPart.reportPartContent.getCrossFilteringInfo());

    return needUpdatePreview(reportPart, invoke('getDrilldownInfo', reportPartContent), null, null, printingMode).then(
      () => {
        store && store.loadReportPartDataByCrossFiltering(reportPart.id);
      }
    );
  };

  onDrilldown = action(drilldown => {
    const { reportBody, publishDrillInfo, store } = this.props;
    const reportPart = this.props.reportPart || {};
    const reportPartContent = reportPart.reportPartContent;
    // Add more info about drilling down field
    if (
      !flow(
        get('point'),
        isNil
      )(drilldown)
    ) {
      const fieldAlias = get('point.nameField.field.name', drilldown);
      $izenda.extend(true, drilldown.point.drillingDownField, {
        reportPartElm: find(
          fieldElement => get('field.fieldNameAlias', fieldElement) == fieldAlias,
          reportPart.fieldCollection
        )
      });
    }

    reportPartContent.updateDrilldownInfo(drilldown);
    if (!reportPartContent.isCrossFiltering) return;

    const drillDownData = {
      filters: [],
      expandedLevel: 0
    };

    const points = reportPartContent.getPoints();
    if (!isEmpty(drilldown) && !isEmpty(points)) {
      drillDownData.expandedLevel = 1; // This value is use for bill cross filtering, it is not updated into map content.

      forEach(p => {
        const fieldName = get('point.nameField.field.name', p);
        !isEmpty(p.mapInfo.backgroundMap.value) &&
        fieldName &&
        drillDownData.filters.push({
          key: fieldName,
          value: p.value,
          field: get('point.drillingDownField', p)
        });
      }, points);
    }

    reportBody && reportBody.updateCrossFiltering(reportPart, drillDownData);
    !!publishDrillInfo && publishDrillInfo(reportPart.id, reportPart.reportPartContent.getCrossFilteringInfo());
    reportPartContent.isCrossFiltering && store && store.loadReportPartDataByCrossFiltering(reportPart.id);
  });

  checkReloadData = () => {
    const reportPart = this.props.reportPart || {};
    const reportPartContent = reportPart.reportPartContent;
    const isBeingBuild = reportPartContent.isBeingBuild;
    const isBeingBuildByView = this.isBeingBuildByView;
    const isChangeMapType = this.previousMapType !== reportPartContent.chartType;

    if (!isBeingBuild || !isBeingBuildByView) {
      return false;
    }

    this.previousMapType = reportPartContent.chartType;
    const activePointOption = reportPartContent.activePointOption || {};
    const activePointOptionByView = this.activePointOptionByView || {};

    return activePointOptionByView.field.fieldNameAlias !== activePointOption.field.fieldNameAlias || isChangeMapType;
  };

  updateShownRecords = shownRecords => {
    this.setState({ shownRecords });
  };

  render() {
    const {
      printingMode,
      refreshData,
      isPreview,
      style,
      finishedRender,
      fulfilledData,
      hideGridPopup
    } = this.props;
    const reportPart = this.props.reportPart || {};
    const reportPartContent = reportPart.reportPartContent;
    const chartProperties = this.getChartProperties(this.props) || {};
    const type = reportPartContent.type;

    // check - if [activePointOption] is changed > get new previewData
    const needReloadData = this.checkReloadData(reportPartContent);

    this.saveCurrentReportState(reportPartContent);

    const chartData =
      reportPartContent.isBeingBuild &&
      chartDataParser(reportPartContent.activeElmsByContainers, reportPartContent.expandedLevel, {
        fieldsMapping: reportPart.previewDataFieldsMapping,
        records: reportPart.previewDataRecords,
        rawData: reportPart.previewData,
        cities: reportPart.cities,
        addressCodes: reportPart.addressCodes,
        countryCodes: reportPart.countryCodes,
        postalCodes: reportPart.postalCodes,
        stateCodes: reportPart.stateCodes,
        countyCodes: reportPart.countyCodes
      });

    // for refreshData - report part properties
    refreshData && refreshData(true);

    if (ReportPartUtils.needToRenderLazyLoadingBar(reportPart, printingMode)) {
      return ReportPartUtils.renderLazyLoadingBar();
    }

    return (
      <MapChart
        type={type}
        vizEngine={reportPartContent.vizEngine}
        reportPart={reportPart}
        isReloadData={needReloadData}
        reloadData={this.reloadData}
        isPreview={isPreview}
        chartType={chartProperties.chartType}
        chartOptions={chartProperties}
        seriesOptions={reportPart.fieldCollection}
        printingMode={printingMode}
        finishedRender={finishedRender}
        fulfilledData={fulfilledData}
        isLoading={reportPart.isLoadingData}
        style={printingMode && style ? style : {}}
        chartData={chartData}
        updateShownRecords={this.updateShownRecords}
      >
        {!printingMode && (
          <MapToolbar
            hideGridPopup={hideGridPopup}
            reportPart={reportPart}
            onDrillDown={this.onDrilldown}
            shownRecords={this.state.shownRecords}
          />
        )}
      </MapChart>
    );
  }
}

@connect()
export default class MapFrontSide extends Component {
  componentDidMount() {
    !uiState.isMapDataAvailable && ProvisionMapDataStore.getProvisioningStatus();
  }

  shouldComponentUpdate(nextProps: Object) {
    return !isEqual(this.props, nextProps);
  }

  render() {
    const { hideTitle, width, printingMode } = this.props;
    const reportPartDetails = this.props.reportPart || {};
    const sizeStyle = this.props.style || {};
    const isMapDataAvailable = uiState.isMapDataAvailable;
    const classes = classnames({
      'report-part-gauge-front-side': true,
      'izenda-ReportPartMap_frontSide': true,
      'izenda-ReportPart-smallSize': width < CONSTANT.REPORT_PART_SMALL_SIZE
    });

    return printingMode ? (
      <div className="report-part-chart-front-side" style={PrintingUtils.getReportPartContainerStyle(this.props)}>
        <div className={PrintingUtils.getReportPartClassName(this.props)} style={sizeStyle}>
          {!hideTitle && <ReportPartTitle mode={REPORT_PART_MODE.PREVIEW} reportPart={reportPartDetails}/>}
          {isMapDataAvailable ? (
            <MapPreview {...omit(['width', 'height'], this.props)} style={sizeStyle}/>
          ) : (
            <span>{CONSTANT.MESSAGES.MAP_DATA_UNAVAILABLE}</span>
          )}
        </div>
      </div>
    ) : (
      <VerticalLayout {...this.props} className={classes}>
        <ReportPartTitle mode={REPORT_PART_MODE.PREVIEW} reportPart={reportPartDetails}/>

        {isMapDataAvailable ? (
          <MapPreview {...omit(['width', 'height'], this.props)} />
        ) : (
          <span>{CONSTANT.MESSAGES.MAP_DATA_UNAVAILABLE}</span>
        )}
      </VerticalLayout>
    );
  }
}



// WEBPACK FOOTER //
// ./ui/modules/ReportDesigner/Fields/ReportBody/ReportPartType/ReportPartMap/Izenda.MapFrontSide.js